/**
 * SelectWithCustom (NC-1102, Feat 2.0 #1090) — curated dropdown with a
 * deliberate free-text override ("Andere…"). Generalizes the runtime-SKU
 * sentinel pattern from WorkloadEditorPage (NC-974) so packerConfig fields
 * stop being silent free-text traps for admins.
 *
 * Contract:
 * - `options` are SUGGESTIONS; the server stays tolerant (override must work).
 * - A value outside the list renders as active custom value — never silently
 *   swapped to a list entry (existing docs keep their exact values).
 * - Selecting "Andere…" keeps the current value and opens the text field.
 */

import React, { useEffect, useState } from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';

const CUSTOM_SENTINEL = '__custom__';

export interface SelectWithCustomProps {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  helperText?: string;
  customLabel?: string;
  customHelperText?: string;
  disabled?: boolean;
}

export function SelectWithCustom({
  label,
  value,
  options,
  onChange,
  helperText,
  customLabel,
  customHelperText,
  disabled,
}: SelectWithCustomProps) {
  const isListed = options.includes(value);
  const [customMode, setCustomMode] = useState<boolean>(!!value && !isListed);

  // Values loaded asynchronously (edit mode) may arrive after mount — flip to
  // custom when an out-of-list value appears, so nothing is silently lost.
  useEffect(() => {
    if (value && !options.includes(value)) {
      setCustomMode(true);
    }
  }, [value, options]);

  const handleSelect = (selected: string) => {
    if (selected === CUSTOM_SENTINEL) {
      setCustomMode(true);
      return; // keep the current value; the text field takes over
    }
    setCustomMode(false);
    onChange(selected);
  };

  return (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          select
          label={label}
          value={customMode ? CUSTOM_SENTINEL : value}
          onChange={(e) => handleSelect(e.target.value)}
          fullWidth
          disabled={disabled}
          helperText={helperText}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM_SENTINEL}>Andere…</MenuItem>
        </TextField>
      </Grid>
      {customMode && (
        <Grid item xs={12} md={6}>
          <TextField
            label={customLabel ?? `${label} (eigener Wert)`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            disabled={disabled}
            error={!value}
            helperText={!value ? 'Eigener Wert darf nicht leer sein.' : customHelperText}
          />
        </Grid>
      )}
    </>
  );
}
